import { FadeIn } from "@/components/Reveal";
import ChapterHeader from "@/components/ChapterHeader";
import { Link } from "react-router-dom";
import { Users, Building2, Check, ArrowUpRight } from "lucide-react";
import residencyImg from "@/assets/club-pool-residency.webp";

const PRIVATE_BENEFITS = [
  "Full access to Club Tikratoli & Sunshine Kitchen restaurant @ 25% discount",
  "Bablu Da Theka, bar @ 25% discount (Container Bar — first of its kind in Jharkhand)",
  "Staycation for overnight stay @ 10% discount on room tariff (First of its kind building in Jharkhand)",
  "Pool usage for club members — one hour / day, free",
  "Indoor / outdoor games @ 50% discount",
  "ATV & Jeep rides @ 25% discount",
  "Special events (e.g. 31st December New Year Eve Gala dinner) — 10% discount for members. Guest charges will apply as per the event.",
];

const CORPORATE_BENEFITS = [
  "All private member discounts apply — but only for the designated corporate members, their spouses & children",
  "Should the member / company host a function at Tikratoli Staycation / Pool Side — 10% discount applies",
  "10% discount on special events (e.g. 31st December Gala dinner)",
];

export default function ClubTikratoli() {
  return (
    <main data-testid="club-tikratoli-page" className="pb-24">
      {/* Hero — full-bleed image with floating text (matches Home hero) */}
      <section data-testid="club-hero" className="relative min-h-[100svh] overflow-hidden flex items-end">
        <img
          data-testid="club-hero-image"
          src={residencyImg}
          alt="Nilika Lall Residency overlooking the pool at Club Tikratoli"
          className="absolute inset-0 w-full h-full object-cover object-[60%_center]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1913]/70 via-[#1C1913]/25 to-[#1C1913]" />
        <div className="relative z-10 w-full mx-auto max-w-[1600px] px-6 lg:px-12 pt-40 pb-20 md:pb-28">
          <FadeIn>
            <div className="stamp mb-8 bg-[#1C1913]/55 backdrop-blur-sm text-[#F2E7D0]">Vol · XI · Club Tikratoli</div>
            <h1
              className="font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.92] tracking-tighter text-[#F2E7D0] max-w-5xl"
              style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55), 0 8px 40px rgba(0,0,0,0.35)" }}
            >
              Join <em className="italic text-[#D97645]">Club Tikratoli</em>.
            </h1>
            <p
              className="mt-8 max-w-2xl text-[#F2E7D0]/90 text-xl md:text-2xl leading-snug font-display"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}
            >
              Two memberships — Private &amp; Corporate — unlocking a lifetime of dining, staycations,
              jeep rides, pool afternoons and private events on the estate.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Private Membership */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20">
        <ChapterHeader num="01" kicker="Private Membership" title="For individuals & families." />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <div className="border border-[#D97645]/40 bg-[#27221C] p-8">
              <div className="flex items-center gap-3 mb-6">
                <Users size={22} className="text-[#D97645]" />
                <div className="font-mono-label text-[11px] tracking-[0.28em] text-[#D97645]">LIFE MEMBERSHIP</div>
              </div>
              <div className="font-display text-5xl md:text-6xl text-[#F2E7D0] leading-[1]">₹ 50,000</div>
              <div className="mt-2 font-mono-label text-[#B8A891] text-xs">+ GST · NON-REFUNDABLE</div>
              <div className="mt-8 pt-6 border-t border-white/10 space-y-3 text-sm text-[#B8A891]">
                <div><span className="text-[#F2E7D0]">Inclusive</span> for spouse &amp; children below 18 years</div>
                <div>Children <span className="text-[#F2E7D0]">18–25</span> → pay 50% of annual subscription</div>
                <div><span className="text-[#F2E7D0]">"Specially Abled"</span> dependents → dependent members for life</div>
                <div>Children above 25 → apply for full life membership, or come as a guest</div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-mono-label text-[11px] tracking-[0.2em] text-[#D97645]">ANNUAL SUBSCRIPTION — FROM YEAR TWO</div>
                <div className="mt-2 font-display text-3xl text-[#F2E7D0]">₹ 3,000 <span className="text-sm text-[#B8A891] font-mono-label align-middle">+ GST / year</span></div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-mono-label text-[11px] tracking-[0.2em] text-[#D97645]">GUEST CHARGE</div>
                <div className="mt-2 font-display text-2xl text-[#F2E7D0]">₹ 150 <span className="text-sm text-[#B8A891] font-mono-label align-middle">flat / person / visit</span></div>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.1} className="lg:col-span-7">
            <ul className="space-y-4" role="list">
              {PRIVATE_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-4 border-b border-white/10 pb-4">
                  <Check size={18} className="text-[#D97645] mt-1 shrink-0" />
                  <span className="text-[#F2E7D0] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* Corporate Membership */}
      <section className="bg-[#27221C] py-20 mt-6">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <ChapterHeader num="02" kicker="Corporate Membership" title="For companies & teams." />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <FadeIn className="lg:col-span-5">
              <div className="border border-[#D97645]/40 bg-[#1C1913] p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 size={22} className="text-[#D97645]" />
                  <div className="font-mono-label text-[11px] tracking-[0.28em] text-[#D97645]">CORPORATE · 5 YEARS</div>
                </div>
                <div className="font-display text-5xl md:text-6xl text-[#F2E7D0] leading-[1]">₹ 3,00,000</div>
                <div className="mt-2 font-mono-label text-[#B8A891] text-xs">+ GST · VALID FOR 5 YEARS</div>
                <div className="mt-8 pt-6 border-t border-white/10 space-y-3 text-sm text-[#B8A891]">
                  <div>Valid for <span className="text-[#F2E7D0]">5 nominated corporate members</span>, their spouses &amp; children below 18</div>
                  <div><span className="text-[#F2E7D0]">"Add-on"</span> corporate members — limited to 2 members @ ₹ 1,00,000 each / year + GST</div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-mono-label text-[11px] tracking-[0.2em] text-[#D97645]">ANNUAL CORPORATE SUBSCRIPTION</div>
                  <div className="mt-2 font-display text-3xl text-[#F2E7D0]">₹ 31,500 <span className="text-sm text-[#B8A891] font-mono-label align-middle">+ GST / annum</span></div>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} className="lg:col-span-7">
              <ul className="space-y-4" role="list">
                {CORPORATE_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-4 border-b border-white/10 pb-4">
                    <Check size={18} className="text-[#D97645] mt-1 shrink-0" />
                    <span className="text-[#F2E7D0] leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Note + CTA */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <FadeIn className="md:col-span-7">
            <div className="border-l-2 border-[#D97645] pl-6">
              <div className="font-mono-label text-[11px] tracking-[0.25em] text-[#D97645] mb-3">NOTE</div>
              <p className="text-[#B8A891] leading-relaxed text-base">
                Rates &amp; tariffs will be adjusted from time to time. All rates indicated are <span className="text-[#F2E7D0]">+ GST</span>.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1} className="md:col-span-5">
            <Link
              to="/contact"
              data-testid="club-apply-cta"
              className="inline-flex items-center gap-3 bg-[#D97645] hover:bg-[#C0602F] transition text-[#F2E7D0] px-8 py-5 font-mono-label"
            >
              Enquire about membership <ArrowUpRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
